import type { DialogueTree } from './types';

/**
 * Email dialogue trees, keyed by sender NPC id (matches `email.fromNpc`).
 *
 * Email replies are mostly single-turn (NPC reply per choice; few cascading
 * follow-ups) because email isn't the chat shape. Voice is more formal than
 * the phone counterparts — these are written communications.
 */

const MOM_EMAIL: DialogueTree = {
  contactId: 'mom',
  rootChoices: [
    { id: 'thanks-mom', label: 'thank her warmly',
      playerSays: "thanks for thinking of me mom. love you.",
      effects: [{ type: 'rep', target: 'mom', value: 5 }, { type: 'sanity', value: 3 }],
      responses: [
        { text: "sweetie that means so much. proud of you.\n\nthe brisket is in the freezer. label says \"FOR SWEETIE.\"\n\nlove mom" },
        { text: "❤️❤️❤️\n\ni read this twice. then i read it to your father. he pretended he didn\'t hear me.\n\nlove mom" },
      ],
    },
    { id: 'mom-yes-stock', label: 'pretend to look into the stock',
      playerSays: "i\'ll look into $RUGS. don\'t mortgage the house. seriously mom.",
      responses: [
        { text: "ok sweetie. i won\'t mortgage the house. i\'ll mortgage the GARAGE. that\'s separate, i checked.\n\n(i did not check.)\n\nlove mom" },
      ],
    },
    { id: 'mom-no-stock', label: 'forbid her',
      playerSays: "MOM. do NOT buy $RUGS. do NOT mortgage anything. promise me.",
      effects: [{ type: 'rep', target: 'mom', value: 4 }, { type: 'note', value: 'protected mom from $RUGS rugpull. mom rep +4' }],
      responses: [
        { text: "okay sweetie i promise.\n\nokay janet just told me she sold. so we\'re fine. i think.\n\nlove mom" },
      ],
    },
    { id: 'mom-call-sister', label: 'promise to call your sister',
      playerSays: "i\'ll call her tonight. promise.",
      effects: [{ type: 'rep', target: 'mom', value: 6 }],
      responses: [
        { text: "thank you sweetie. she\'s lonely. we\'re all a little lonely. love mom." },
      ],
    },
  ],
};

const BROKER_EMAIL: DialogueTree = {
  contactId: 'broker',
  rootChoices: [
    { id: 'broker-acknowledge', label: 'acknowledge professionally',
      playerSays: "Acknowledged. Will review the agreement.",
      responses: [
        { text: "Thank you for the prompt acknowledgment.\n\nFor the avoidance of doubt: \"will review\" is not, legally, a substitute for \"have reviewed.\" The disclosure is 411 pages. We track who reads it. We see you have not reached page 6.\n\n— Compliance, RobbingTheHood Inc." },
      ],
    },
    { id: 'broker-question-fees', label: 'question the fees',
      playerSays: "I\'d like clarification on the fee schedule.",
      responses: [
        { text: "Per Section 4.2(b)(iii), our fee schedule is disclosed in Section 14.7, which references Appendix C, which is a separate document available upon written request via the form in Section 9.4.\n\nWe are happy to provide further clarification at our standard clarification rate (\$220 / hr).\n\n— Compliance" },
      ],
    },
    { id: 'broker-cease', label: 'demand they stop emailing',
      playerSays: "Please cease all non-essential communications.",
      effects: [{ type: 'rep', target: 'broker', value: -3 }, { type: 'note', value: 'asked broker to stop emailing. they will not.' }],
      responses: [
        { text: "Per the brokerage agreement, all communications from RobbingTheHood Inc. are deemed essential by RobbingTheHood Inc.\n\nWe will continue to communicate. Frequency will not change. Have a great day.\n\n— Compliance" },
      ],
    },
  ],
};

const LAWYER_EMAIL: DialogueTree = {
  contactId: 'lawyer',
  rootChoices: [
    { id: 'lawyer-acknowledge', label: 'acknowledge and follow instructions',
      playerSays: "Understood. I will stop posting and stop emailing the SEC.",
      effects: [{ type: 'cash', value: -700 }, { type: 'note', value: '-$700 — lawyer billed half-hour for the read' }, { type: 'rep', target: 'lawyer', value: 4 }],
      responses: [
        { text: "Good.\n\nThis email took me 22 minutes to read because I had to stop and process that you actually agreed to something. I am billing for the full hour.\n\n— Lou Markowitz, Esq." },
      ],
    },
    { id: 'lawyer-fire', label: 'attempt to fire him',
      playerSays: "You\'re fired. Effective immediately.",
      oneShot: true,
      effects: [{ type: 'cash', value: -1400 }, { type: 'note', value: 'tried to fire lawyer by email. -$1,400. he\'s still your lawyer.' }],
      responses: [
        { text: "Per Clause 14(c) of the engagement letter, you cannot terminate by email. You require a notarized letter, settlement of the outstanding invoice, and replacement counsel of record before any active filing deadline.\n\nYour active filing deadline is Thursday.\n\nI remain your counsel. \$1,400 for this exchange.\n\n— Lou" },
      ],
    },
    { id: 'lawyer-question-bill', label: 'question the bill',
      playerSays: "Lou — I\'m looking at this bill. Some of these line items don\'t make sense.",
      effects: [{ type: 'cash', value: -1400 }, { type: 'note', value: '-$1,400 — lawyer billed for the bill discussion' }],
      responses: [
        { text: "Each line item is real. Each line item is documented. Each line item references either an action I took, an action I considered, or an action I declined to take on your behalf.\n\nDeclining to take an action is also work. That\'s on page 3 of the engagement letter.\n\n\$1,400 for this exchange.\n\n— Lou" },
      ],
    },
  ],
};

const SPONSOR_EMAIL: DialogueTree = {
  contactId: 'sponsor',
  rootChoices: [
    { id: 'sponsor-accept', label: 'accept the sponsorship',
      playerSays: "I\'m in. Let\'s do it.",
      effects: [{ type: 'cash', value: 250 }, { type: 'rep', target: 'lawyer', value: -2 }, { type: 'note', value: '+$250/mo BrainJuice sponsorship. lawyer disapproves.' }],
      responses: [
        { text: "Amazing!! Welcome to the BrainJuice family.\n\nFirst payment of \$250 has been issued. In BrainJuice. The conversion to USD is, currently, locked.\n\nWe\'ll be in touch about your bio update. Required language: \"powered by BRAINJUICE.\"\n\n— Tyler @ BrainJuice" },
      ],
    },
    { id: 'sponsor-counter', label: 'counter at \$5K/mo',
      playerSays: "I\'ll do it for \$5,000/month. Cash. Not BrainJuice tokens.",
      responses: [
        { text: "Let me check with the team.\n\n…\n\nThe team\'s response: \"lol no.\"\n\nWe can offer you \$300/month (in BrainJuice). And we will throw in 2 cases of BrainJuice. Note: BrainJuice is currently in voluntary recall in 14 states. Not for safety. For taste.\n\n— Tyler" },
      ],
    },
    { id: 'sponsor-decline', label: 'decline politely',
      playerSays: "I\'ll pass for now. Thanks anyway.",
      effects: [{ type: 'note', value: 'declined BrainJuice sponsorship. they will email you again next week.' }],
      responses: [
        { text: "Understood. We\'ll re-reach out in approximately 6 days.\n\nAnd then again in 6 days after that.\n\nNo hard feelings.\n\n— Tyler" },
      ],
    },
    { id: 'sponsor-mock', label: 'roast their product',
      playerSays: "BrainJuice is just sugar water with a logo on it. Hard pass.",
      effects: [{ type: 'tribeRep', target: 'crypto', value: -2 }, { type: 'notoriety', value: 2 }, { type: 'note', value: 'roasted BrainJuice. small notoriety bump. crypto bros oddly mad.' }],
      responses: [
        { text: "We\'ve forwarded your reply to legal.\n\nWe\'ve also screencapped it. Tyler is, in this email\'s words, \"a little hurt.\"\n\nThe BrainJuice community is, technically, watching.\n\n— Tyler" },
      ],
    },
  ],
};

const SPAM_EMAIL: DialogueTree = {
  contactId: 'spam',
  rootChoices: [
    { id: 'spam-engage', label: 'reply with curiosity',
      playerSays: "Tell me more.",
      effects: [{ type: 'note', value: 'engaged with offshore-shells spam. you will receive 14 more emails by tomorrow.' }],
      responses: [
        { text: "AUTO-REPLY: Excellent! You\'ve been added to our PLATINUM TIER engagement list.\n\nYour personal Concierge will reach out within 4 business hours via email, text, WhatsApp, Telegram, postcard, fax (yes, fax), and Y DM.\n\n— Offshore Shells International" },
      ],
    },
    { id: 'spam-rage', label: 'rage at the spam',
      playerSays: "STOP EMAILING ME. STOP IT.",
      effects: [{ type: 'note', value: 'raged at spam. they will email you twice as much. that\'s how this works.' }],
      responses: [
        { text: "AUTO-REPLY: We\'re sorry to see you reply with hostility.\n\nOur system has interpreted your reply as ENGAGED. We will continue contacting you, more frequently, until your tone improves.\n\nThis is a feature.\n\n— Offshore Shells International" },
      ],
    },
    { id: 'spam-unsubscribe', label: 'click "unsubscribe"',
      playerSays: "[clicks the unsubscribe link]",
      effects: [{ type: 'note', value: 'clicked unsubscribe. signed up for 4 newsletters.' }],
      responses: [
        { text: "AUTO-REPLY: You have been successfully UNSUBSCRIBED from our weekly newsletter.\n\nYou are still subscribed to: our daily newsletter, our \"opportunities\" digest, our \"this week in offshore\" memo, and a podcast you did not know you had been signed up for.\n\nThank you.\n\n— Offshore Shells International" },
      ],
    },
  ],
};

const PLAINTIFF_EMAIL: DialogueTree = {
  contactId: 'plaintiff',
  rootChoices: [
    { id: 'plaintiff-defer-counsel', label: 'defer to counsel (smart)',
      playerSays: "Please direct all communications to my counsel: Lou Markowitz, Esq.",
      effects: [{ type: 'rep', target: 'lawyer', value: 3 }, { type: 'secHeat', value: -2 }, { type: 'note', value: 'deferred plaintiff to counsel. correct move. -2 SEC heat.' }],
      responses: [
        { text: "Counsel for the plaintiff acknowledges. We will redirect all communications to Mr. Markowitz.\n\nFor the record, your decision to retain counsel and use it appropriately is the legally correct move. We mention this not to compliment you, but to note it for the file.\n\n— Class Counsel" },
      ],
    },
    { id: 'plaintiff-defend', label: 'defend yourself in writing (bad)', destructive: true,
      playerSays: "I did nothing wrong. The class action is baseless.",
      effects: [{ type: 'secHeat', value: 8 }, { type: 'rep', target: 'lawyer', value: -10 }, { type: 'note', value: '+8 SEC heat — defended yourself in writing without counsel. Lou is going to be furious.' }],
      responses: [
        { text: "Counsel for the plaintiff is in receipt.\n\nYour written defense, made without counsel, has been entered as Exhibit A.\n\nWe will be amending the complaint to incorporate your specific framing. Thank you for the additional material.\n\n— Class Counsel" },
      ],
    },
    { id: 'plaintiff-settle', label: 'inquire about settlement',
      playerSays: "What would settlement look like?",
      effects: [{ type: 'note', value: 'opened settlement door with plaintiff. number forthcoming.' }],
      responses: [
        { text: "Counsel for the plaintiff is encouraged.\n\nA reasonable settlement, given the current evidence, would be \$2.4M plus our legal fees. We will provide a full breakdown via separate communication.\n\nIf this is unworkable, we are open to discussion. The discussion will only adjust the figure modestly. The figure is essentially the figure.\n\n— Class Counsel" },
      ],
    },
  ],
};

const IRS_EMAIL: DialogueTree = {
  contactId: 'irs',
  rootChoices: [
    { id: 'irs-acknowledge', label: 'acknowledge formally',
      playerSays: "Acknowledged. I will respond by the deadline.",
      responses: [
        { text: "Per your reply: noted.\n\nAcknowledgment is not a substitute for filing. Filing is not a substitute for payment. Payment is not a substitute for the additional payment.\n\nYou have 14 days.\n\n— Internal Revenue Service" },
      ],
    },
    { id: 'irs-extension', label: 'request extension',
      playerSays: "Requesting a 60-day extension.",
      effects: [{ type: 'irsHeat', value: 4 }, { type: 'note', value: 'requested IRS extension. +4 IRS heat. extension may or may not be granted.' }],
      responses: [
        { text: "Per your reply: extension requests are processed in approximately 8 to 11 weeks.\n\nYour original deadline remains in effect during processing. Penalties accrue during processing.\n\nThank you for using IRS Online.\n\n— Internal Revenue Service" },
      ],
    },
    { id: 'irs-rage', label: 'rage at the IRS', destructive: true,
      playerSays: "This is theft. You are thieves.",
      effects: [{ type: 'irsHeat', value: 12 }, { type: 'fbiHeat', value: 4 }, { type: 'note', value: 'raged at the IRS in writing. +12 IRS heat / +4 FBI heat. file flagged.' }],
      responses: [
        { text: "Per your reply: hostile communications with the Service have been logged.\n\nYour file has been escalated to a senior agent. A senior agent has not, in 7 years, escalated any file downward. We do not know if it is possible.\n\n— Internal Revenue Service" },
      ],
    },
  ],
};

const FAN_EMAIL: DialogueTree = {
  contactId: 'fan',
  rootChoices: [
    { id: 'fan-thanks', label: 'thank them genuinely',
      playerSays: "thanks for the kind words. means a lot.",
      effects: [{ type: 'rep', target: 'best_friend', value: 1 }, { type: 'sanity', value: 2 }],
      responses: [
        { text: "OH MY GOD U REPLIED 😭😭😭\n\ngonna print this out and frame it. mom is gonna lose her mind. THANK U KING/QUEEN" },
      ],
    },
    { id: 'fan-meet', label: 'agree to meet up',
      playerSays: "let\'s meet up sometime",
      effects: [{ type: 'sanity', value: -3 }, { type: 'note', value: 'agreed to meet a fan IRL. you have not thought this through.' }],
      responses: [
        { text: "OMG WHEN. WHERE. I\'LL DRIVE ANYWHERE.\n\ni already booked a hotel near your zip code. i don\'t know your zip code. i guessed. i\'m correct.\n\n💯💯💯" },
      ],
    },
    { id: 'fan-gentle-no', label: 'set a gentle boundary',
      playerSays: "appreciate it. let\'s keep things online for now.",
      effects: [{ type: 'rep', target: 'best_friend', value: 0 }, { type: 'note', value: 'gently declined fan meetup. they understand. probably.' }],
      responses: [
        { text: "totally totally totally i get it. parasocial is okay too. parasocial is, like, real to me 💯\n\ni\'ll keep my distance. metaphorically.\n\n📸 PS i made fan art will tag u on Y" },
      ],
    },
  ],
};

const HATER_EMAIL: DialogueTree = {
  contactId: 'hater',
  rootChoices: [
    { id: 'hater-engage', label: 'engage angrily',
      playerSays: "you don\'t know me. you don\'t know anything.",
      effects: [{ type: 'sanity', value: -4 }, { type: 'note', value: 'engaged with a hater. -4 sanity. they will screenshot.' }],
      responses: [
        { text: "screenshotting this for the group chat where we plan your downfall.\n\nyou engaged. you always engage. that\'s the entire game and you keep losing it.\n\n— a hater" },
      ],
    },
    { id: 'hater-ignore', label: 'ignore (don\'t reply)',
      playerSays: "[chose not to reply]",
      effects: [{ type: 'sanity', value: 3 }, { type: 'note', value: 'did not reply to hater. +3 sanity. correct move.' }],
      responses: [
        { text: "[no reply — they\'ll move on. eventually.]" },
      ],
    },
    { id: 'hater-bless', label: '"i hope you find peace"',
      playerSays: "i hope you find peace. genuinely.",
      effects: [{ type: 'sanity', value: 4 }, { type: 'tribeRep', target: 'left', value: 2 }, { type: 'note', value: 'blessed the hater. +4 sanity. left tribe noticed.' }],
      responses: [
        { text: "ew don\'t do that.\n\nstill suing.\n\n— a hater" },
      ],
    },
  ],
};

const PODCAST_EMAIL: DialogueTree = {
  contactId: 'podcast',
  rootChoices: [
    { id: 'podcast-accept', label: 'accept the booking',
      playerSays: "I\'m in. Let\'s book it.",
      effects: [{ type: 'arc', value: 'podcastAppearance' }, { type: 'note', value: 'accepted podcast booking. arc starting.' }],
      responses: [
        { text: "Amazing. We\'ll send a contract.\n\nSlot is in 14 days. Episode length: 2.5 hours. Topic: open. Sponsor reads: 3 mandatory (BetterHelp, RaytheonSocks, an as-yet-undisclosed coin).\n\nProducer will reach out.\n\n— Booking" },
      ],
    },
    { id: 'podcast-decline', label: 'pass for now',
      playerSays: "Can\'t do it right now. Maybe in a few months.",
      responses: [
        { text: "Understood. We\'ll book your competitor instead.\n\nWe will reach out again in approximately 11 weeks, by which time your competitor will have done the show and made you look bad.\n\n— Booking" },
      ],
    },
    { id: 'podcast-rate', label: 'demand a rate sheet',
      playerSays: "What\'s the rate? I don\'t do these for free.",
      responses: [
        { text: "We do not pay guests. We pay producers, lavishly.\n\nThe value of the appearance is, allegedly, exposure. Exposure has, historically, killed people. Specifically, in mountaineering. We acknowledge this and stand by the term.\n\n— Booking" },
      ],
    },
  ],
};

const DAD_EMAIL: DialogueTree = {
  contactId: 'dad',
  rootChoices: [
    { id: 'thanks-dad', label: 'thank him sincerely',
      playerSays: "thanks for sending this dad. love you.",
      effects: [{ type: 'sanity', value: 4 }],
      responses: [
        { text: "ok son.\n\nlove you too.\n\ndad" },
      ],
    },
    { id: 'ask-dad-status', label: 'ask if everything\'s ok',
      playerSays: "everything ok dad?",
      effects: [{ type: 'sanity', value: 3 }],
      responses: [
        { text: "ok son.\n\neverything\'s fine. your mother is fine. the dog is fine. the car is mostly fine.\n\ncheck on your sister.\n\ndad" },
      ],
    },
    { id: 'short-reply', label: 'reply just "ok"',
      playerSays: "ok",
      responses: [
        { text: "ok son.\n\ndad" },
      ],
    },
  ],
};

const SISTER_EMAIL: DialogueTree = {
  contactId: 'sister',
  rootChoices: [
    { id: 'sister-warm', label: 'reply warmly',
      playerSays: "thanks for the email. let\'s catch up soon.",
      effects: [{ type: 'rep', target: 'mom', value: 2 }, { type: 'sanity', value: 3 }],
      responses: [
        { text: "i\'ll believe it when i see it on the calendar.\n\nbut. okay. i love you. text me when you actually have a time.\n\n— your sister" },
      ],
    },
    { id: 'sister-rsvp-yes', label: 'RSVP yes to wedding',
      playerSays: "RSVPing yes. plus one. wife is coming. we\'ll be there.",
      effects: [{ type: 'rep', target: 'mom', value: 6 }, { type: 'rep', target: 'wife', value: 2 }, { type: 'note', value: 'RSVP\'d yes to sister\'s wedding. mom rep +6 / wife rep +2.' }],
      responses: [
        { text: "OH MY GOD.\n\nokay. table 4. you\'re sitting next to uncle barry. i\'m sorry. i had to. he asked.\n\nlove you. love wife. see you in october.\n\n— your sister" },
      ],
    },
    { id: 'sister-rsvp-no', label: 'RSVP no (work conflict)',
      playerSays: "won\'t make it — work conflict. so sorry.",
      destructive: true,
      effects: [{ type: 'rep', target: 'mom', value: -10 }, { type: 'note', value: 'declined sister\'s wedding. mom rep -10. this will follow you.' }],
      responses: [
        { text: "ok.\n\nokay. \"work conflict.\" sure.\n\nyou\'ve missed: my graduation, my engagement party, our parents\' anniversary. and now this.\n\ni\'m not mad. i\'m past mad. the math is just clear.\n\n— your sister" },
      ],
    },
  ],
};

export const EMAIL_REPLY_TREES: Record<string, DialogueTree> = {
  mom: MOM_EMAIL,
  dad: DAD_EMAIL,
  sister: SISTER_EMAIL,
  broker: BROKER_EMAIL,
  lawyer: LAWYER_EMAIL,
  sponsor: SPONSOR_EMAIL,
  spam: SPAM_EMAIL,
  plaintiff: PLAINTIFF_EMAIL,
  irs: IRS_EMAIL,
  fan: FAN_EMAIL,
  hater: HATER_EMAIL,
  podcast: PODCAST_EMAIL,
};

export function getEmailReplyTree(fromNpc: string): DialogueTree | undefined {
  return EMAIL_REPLY_TREES[fromNpc];
}
